import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Lock, Waveform, Calendar, ShieldCheck, FileArrowDown, Warning, Pencil, CheckCircle, Clock, X, Heart } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useSiteSettings } from "@/context/SiteSettingsContext";

// QuietRoom palette — deep midnight indigo + plum + warm peach + pearl ink (NO beige, NO green)
const NIGHT = "#14143F";
const NIGHT_2 = "#1A1748";
const NIGHT_3 = "#252070";
const PLUM = "#6B2C5C";
const PEACH = "#F4A88E";
const PEARL = "#F6F3EE";
const TYPE_LIGHT = "#C8C0E8";
const TYPE_MUTED = "#9690C7";
const ALERT = "#3A1F2F";
const STONE = "#E5DFD2";

const SEED_CLIENTS = [
  { id: "c1", initials: "SK", name: "Sarah K.", time: "10:00 — 11:00", session: 4, total: 6, modality: "CBT · Anxiety", consent: true, mood: 7, audio: 2 },
  { id: "c2", initials: "JM", name: "James M.", time: "11:30 — 13:00", session: 1, total: 6, modality: "Hypnotherapy · Intake", consent: false, mood: null, audio: 0 },
  { id: "c3", initials: "EL", name: "Elena L.", time: "14:00 — 15:00", session: 7, total: null, modality: "Couples · Ongoing", consent: true, mood: 7, audio: 0 },
];

const TABS = [
  { key: "today", label: "Today" },
  { key: "file", label: "Client file" },
  { key: "practice", label: "Practice" },
];

export default function QuietRoom() {
  const { settings } = useSiteSettings();
  const [tab, setTab] = useState("today");
  const [activeClient, setActiveClient] = useState("c1");
  const [lateCancel, setLateCancel] = useState({ show: true, charged: null });
  const navigate = useNavigate();

  const handleCharge = (charge) => {
    setLateCancel({ show: false, charged: charge });
    toast.success(charge ? "£75 late-cancellation fee charged to David R." : "Fee waived for David R.");
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: NIGHT, color: PEARL, fontFamily: "'Outfit', sans-serif" }} data-testid="quietroom-interactive">
      <header className="sticky top-0 z-40 border-b" style={{ backgroundColor: NIGHT_2, borderColor: NIGHT_3 }}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/demos/quietroom" className="mr-1" style={{ color: TYPE_LIGHT, opacity: 0.7 }}><ArrowLeft size={18} /></Link>
            <div className="h-9 w-9 flex items-center justify-center" style={{ backgroundColor: PLUM }}>
              <Lock size={16} weight="fill" color={PEACH} />
            </div>
            <div>
              <div className="text-base font-bold leading-none">Dr. Mara Holt, MBACP <span className="font-mono text-[10px]" style={{ color: TYPE_MUTED }}>· Concept mock</span></div>
              <div className="text-[10px] font-mono uppercase tracking-widest mt-0.5" style={{ color: PEACH }}>Encrypted practice diary</div>
            </div>
          </div>
          <button onClick={() => navigate("/consultation?demo=quietroom")} className="text-xs font-bold px-3 py-1.5 whitespace-nowrap" style={{ backgroundColor: PEACH, color: NIGHT }} data-testid="quietroom-cta">
            Co-build yours <ArrowRight size={12} weight="bold" className="inline ml-1" />
          </button>
        </div>
        <nav className="max-w-6xl mx-auto px-6 flex gap-6 border-t" style={{ borderColor: NIGHT_3 }}>
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} className="relative py-3 text-sm font-semibold" style={{ color: tab === t.key ? PEARL : TYPE_MUTED }} data-testid={`quietroom-tab-${t.key}`}>
              {t.label}
              {tab === t.key && <span className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: PEACH }} />}
            </button>
          ))}
        </nav>
      </header>

      <section className="max-w-6xl mx-auto px-6 py-8">
        {tab === "today" && <TodayTab onPick={(id) => { setActiveClient(id); setTab("file"); }} lateCancel={lateCancel} onCharge={handleCharge} />}
        {tab === "file" && <ClientFileTab clientId={activeClient} setActiveClient={setActiveClient} />}
        {tab === "practice" && <PracticeTab />}
      </section>

      {settings.co_innovation_enabled && (
        <footer className="border-t mt-8 py-12" style={{ backgroundColor: NIGHT_2, borderColor: NIGHT_3 }}>
          <div className="max-w-3xl mx-auto px-6 text-center">
            <p className="text-xs font-mono uppercase tracking-widest mb-3" style={{ color: PEACH }}>Co-innovation candidate</p>
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter mb-4" style={{ color: PEARL }}>Be the founding therapist.</h2>
            <p className="text-base mb-6 max-w-lg mx-auto" style={{ color: TYPE_LIGHT }}>We co-build QuietRoom with one founding solo practitioner. You shape the workflow, you get it free for the first 6 months, and you keep using it forever at half the App Store price.</p>
            <Link to="/consultation?demo=quietroom" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold" style={{ backgroundColor: PEACH, color: NIGHT }} data-testid="quietroom-footer-cta">Co-build QuietRoom with us <ArrowRight size={14} weight="bold" /></Link>
          </div>
        </footer>
      )}
    </div>
  );
}

// ---------- TODAY ----------
function TodayTab({ onPick, lateCancel, onCharge }) {
  return (
    <div>
      <div className="mb-8">
        <p className="text-xs font-mono uppercase tracking-widest mb-2" style={{ color: PEACH }}>Tue 14 Apr · 4 sessions</p>
        <h1 className="text-4xl md:text-5xl font-black tracking-tighter" style={{ color: PEARL }}>Your quiet room.</h1>
        <p className="text-base mt-3 max-w-xl" style={{ color: TYPE_LIGHT }}>Tap any client to open their encrypted file. Notes, packages, and bespoke audio live here — and only here.</p>
      </div>

      <div className="grid gap-4">
        {SEED_CLIENTS.map((c) => (
          <button key={c.id} onClick={() => onPick(c.id)} className="text-left p-5 border transition-colors hover:border-current grid grid-cols-12 gap-4 items-center" style={{ backgroundColor: NIGHT_3, borderColor: "rgba(255,255,255,0.08)", color: PEARL }} data-testid={`quietroom-client-${c.id}`}>
            <div className="col-span-1 flex justify-center">
              <div className="h-12 w-12 rounded-full flex items-center justify-center font-bold" style={{ backgroundColor: PLUM, color: PEACH }}>{c.initials}</div>
            </div>
            <div className="col-span-7">
              <div className="text-lg font-bold">{c.name}<span className="ml-3 text-xs font-mono" style={{ color: TYPE_MUTED }}>· {c.time}</span></div>
              <div className="text-xs font-mono uppercase tracking-widest mt-1" style={{ color: TYPE_LIGHT }}>{c.modality}</div>
              {c.total && (
                <div className="mt-2 flex items-center gap-3">
                  <div className="h-1.5 w-40 rounded-full overflow-hidden" style={{ backgroundColor: NIGHT }}>
                    <div className="h-full" style={{ width: `${(c.session / c.total) * 100}%`, backgroundColor: PEACH }} />
                  </div>
                  <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: TYPE_MUTED }}>Package {c.session}/{c.total}</span>
                </div>
              )}
              {!c.consent && (
                <div className="mt-2 inline-block px-2 py-1 text-[10px] font-mono uppercase tracking-widest font-bold" style={{ backgroundColor: PLUM, color: PEACH }}>⚠ Consent form pending</div>
              )}
              {c.mood && (
                <div className="mt-2 text-[10px] font-mono uppercase tracking-widest" style={{ color: TYPE_MUTED }}>Between-session check-in: mood {c.mood}/10</div>
              )}
            </div>
            <div className="col-span-4 text-right">
              <span className="inline-block px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest font-bold" style={{ backgroundColor: PEACH, color: NIGHT }}>Open file →</span>
            </div>
          </button>
        ))}
      </div>

      {lateCancel.show && (
        <div className="mt-6 p-5 border-2 flex flex-col md:flex-row md:items-center gap-4" style={{ backgroundColor: ALERT, borderColor: PLUM }} data-testid="quietroom-late-cancel">
          <Warning size={24} weight="fill" style={{ color: PEACH }} className="flex-shrink-0" />
          <div className="flex-1">
            <p className="text-xs font-mono uppercase tracking-widest font-bold mb-1" style={{ color: PEACH }}>Late cancellation · David R · 16:00</p>
            <p className="text-sm" style={{ color: TYPE_LIGHT }}>Cancelled 2 hours ago. Your stated 24-hour policy applies. Charge the £75 session fee?</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => onCharge(true)} className="px-4 py-2 text-xs font-bold" style={{ backgroundColor: PEACH, color: NIGHT }} data-testid="quietroom-charge-yes">Yes, charge</button>
            <button onClick={() => onCharge(false)} className="px-4 py-2 text-xs font-bold border" style={{ borderColor: TYPE_MUTED, color: TYPE_LIGHT }}>Waive</button>
          </div>
        </div>
      )}
      {lateCancel.charged === true && (
        <div className="mt-6 p-4 flex items-center gap-3" style={{ backgroundColor: NIGHT_3 }}>
          <CheckCircle size={18} weight="fill" style={{ color: PEACH }} />
          <p className="text-sm" style={{ color: TYPE_LIGHT }}><b style={{ color: PEARL }}>£75 charged</b> to David R · receipt sent · noted in his file under cancellation history.</p>
        </div>
      )}
    </div>
  );
}

// ---------- CLIENT FILE ----------
function ClientFileTab({ clientId, setActiveClient }) {
  const client = SEED_CLIENTS.find((c) => c.id === clientId) || SEED_CLIENTS[0];
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteSaved, setNoteSaved] = useState(false);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* Sidebar */}
      <aside className="lg:col-span-3">
        <p className="text-[10px] font-mono uppercase tracking-widest mb-3" style={{ color: PEACH }}>Active clients</p>
        <div className="grid gap-2">
          {SEED_CLIENTS.map((c) => (
            <button key={c.id} onClick={() => setActiveClient(c.id)} className="text-left p-3 flex items-center gap-3 border" style={{ backgroundColor: c.id === clientId ? NIGHT_3 : "transparent", borderColor: c.id === clientId ? PEACH : "rgba(255,255,255,0.08)", color: PEARL }} data-testid={`quietroom-sidebar-${c.id}`}>
              <div className="h-9 w-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ backgroundColor: PLUM, color: PEACH }}>{c.initials}</div>
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{c.name}</div>
                <div className="text-[9px] font-mono uppercase tracking-widest" style={{ color: TYPE_MUTED }}>{c.modality.split("·")[0]}</div>
              </div>
            </button>
          ))}
        </div>
      </aside>

      {/* Main */}
      <main className="lg:col-span-9">
        <div className="p-5 mb-4" style={{ backgroundColor: NIGHT_2 }}>
          <div className="flex items-center gap-2 mb-2"><Lock size={12} weight="fill" style={{ color: PEACH }} /><p className="text-[10px] font-mono uppercase tracking-widest" style={{ color: PEACH }}>Encrypted client file · CLIENT-2026-{clientId.slice(1).padStart(3, "0")}</p></div>
          <h1 className="text-3xl font-black tracking-tighter" style={{ color: PEARL }}>{client.name}</h1>
          <p className="text-xs font-mono uppercase tracking-widest mt-1" style={{ color: TYPE_MUTED }}>{client.modality}</p>
        </div>

        {/* Package */}
        {client.total && (
          <div className="bg-white p-5 mb-4" style={{ color: NIGHT }} data-testid="quietroom-package">
            <p className="text-[10px] font-mono uppercase tracking-widest font-bold mb-1" style={{ color: PLUM }}>Package balance</p>
            <div className="flex items-baseline gap-3">
              <span className="text-3xl font-black">{client.session} of {client.total}</span>
              <span className="text-sm font-mono" style={{ color: PLUM }}>sessions used</span>
            </div>
            <div className="h-2 mt-3 bg-zinc-100 overflow-hidden">
              <div className="h-full" style={{ width: `${(client.session / client.total) * 100}%`, backgroundColor: PEACH }} />
            </div>
            <p className="text-xs mt-2" style={{ color: PLUM }}>Renews <b>12 May</b> · £540 · auto-prompt at session 5</p>
          </div>
        )}

        {/* Hypnotherapy audio */}
        {client.audio > 0 && (
          <div className="bg-white p-5 mb-4" style={{ color: NIGHT }} data-testid="quietroom-audio">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest font-bold" style={{ color: PLUM }}>Bespoke audio · {client.audio} recordings</p>
                <p className="text-base font-bold mt-1">Sleep induction · v3</p>
              </div>
              <span className="text-[10px] font-mono uppercase tracking-widest font-bold px-2 py-1" style={{ backgroundColor: NIGHT, color: PEACH }}>Expires in 30 days</span>
            </div>
            <div className="flex items-center gap-3 p-3" style={{ backgroundColor: PEARL }}>
              <button className="h-10 w-10 rounded-full flex items-center justify-center" style={{ backgroundColor: NIGHT, color: PEACH }} data-testid="quietroom-audio-play"><Waveform size={16} weight="fill" /></button>
              <div className="flex-1">
                <div className="flex items-end gap-0.5 h-8">
                  {[14, 22, 30, 36, 28, 18, 24, 32, 38, 30, 22, 16, 24, 30, 36, 28, 20, 26, 32, 38, 30, 22, 14, 24, 30, 28, 18].map((h, i) => (
                    <div key={i} className="flex-1" style={{ height: h, backgroundColor: i < 8 ? PEACH : "#D1C5BC" }} />
                  ))}
                </div>
              </div>
              <span className="text-xs font-mono" style={{ color: PLUM }}>11:42</span>
            </div>
            <p className="text-xs mt-3" style={{ color: PLUM }}>Listen-only link · watermarked with the client's email · auto-revokes after 30 days. <span style={{ color: NIGHT }}>No more emailed MP3s flying around.</span></p>
          </div>
        )}

        {/* Notes */}
        <div className="bg-white p-5 mb-4" style={{ color: NIGHT }} data-testid="quietroom-notes">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] font-mono uppercase tracking-widest font-bold" style={{ color: PLUM }}>Session notes · Tue 14 Apr</p>
            <button onClick={() => setNoteOpen(!noteOpen)} className="text-xs font-mono uppercase tracking-widest flex items-center gap-1" style={{ color: PLUM }} data-testid="quietroom-edit-note">
              <Pencil size={12} weight="bold" /> {noteOpen ? "Close" : "Edit"}
            </button>
          </div>
          {!noteOpen ? (
            <div>
              <p className="text-sm leading-relaxed">Reported lower anxiety since the breathing technique. Sleep improved (5h → 6.5h avg). Continue CBT framework. Homework: log three "rumination triggers" before next session.</p>
              <p className="text-[10px] font-mono uppercase tracking-widest mt-3" style={{ color: PLUM }}>Saved 18:23 · auto-encrypted at rest</p>
            </div>
          ) : (
            <div>
              <textarea defaultValue="Reported lower anxiety since the breathing technique. Sleep improved (5h → 6.5h avg). Continue CBT framework. Homework: log three rumination triggers before next session." rows={6} className="w-full p-3 text-sm border" style={{ borderColor: STONE }} data-testid="quietroom-note-textarea" />
              <div className="flex gap-2 mt-3">
                <button onClick={() => { setNoteOpen(false); setNoteSaved(true); toast.success("Note encrypted and saved"); }} className="px-4 py-2 text-xs font-bold" style={{ backgroundColor: NIGHT, color: PEACH }} data-testid="quietroom-save-note">Save & encrypt</button>
                <button onClick={() => setNoteOpen(false)} className="px-4 py-2 text-xs font-bold border" style={{ borderColor: STONE, color: PLUM }}>Cancel</button>
              </div>
            </div>
          )}
          {noteSaved && !noteOpen && <p className="text-[10px] font-mono uppercase tracking-widest mt-3" style={{ color: PEACH }}>✓ Just saved</p>}
        </div>

        {/* Between-session check-in */}
        {client.mood && (
          <div className="p-5 mb-4" style={{ backgroundColor: NIGHT_2 }} data-testid="quietroom-checkin">
            <div className="flex items-center gap-2 mb-2"><Heart size={12} weight="fill" style={{ color: PEACH }} /><p className="text-[10px] font-mono uppercase tracking-widest" style={{ color: PEACH }}>Between-session check-in · powered by ReflectiveMind</p></div>
            <p className="text-sm mb-3" style={{ color: TYPE_LIGHT }}>{client.name.split(" ")[0]} logged <b style={{ color: PEARL }}>mood {client.mood}/10</b> on Sun 12 Apr · sleep 6.5h · "rumination triggers" homework completed.</p>
            <div className="flex items-end gap-1 h-12">
              {[4, 5, 5, 6, 4, 7, 6, 7, client.mood].map((m, i) => (
                <div key={i} className="flex-1" style={{ height: `${m * 10}%`, backgroundColor: i === 8 ? PEACH : PLUM, opacity: i === 8 ? 1 : 0.5 }} />
              ))}
            </div>
            <p className="text-[10px] font-mono uppercase tracking-widest mt-2" style={{ color: TYPE_MUTED }}>9 weeks · trending up · ReflectiveMind premium · client gets it free</p>
          </div>
        )}

        {/* ICO + SAR */}
        <div className="p-4 flex items-center gap-3" style={{ backgroundColor: NIGHT, borderTop: `1px solid ${NIGHT_3}` }}>
          <ShieldCheck size={20} weight="fill" style={{ color: PEACH }} />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-mono uppercase tracking-widest font-bold" style={{ color: PEACH }}>ICO registered · ZA888231 · 7-year retention</p>
            <p className="text-xs" style={{ color: TYPE_LIGHT }}>One-tap Subject Access Request export · auto-redacts third-party names</p>
          </div>
          <button onClick={() => toast.success("SAR export prepared (PDF · 4.2 MB)")} className="px-3 py-2 text-xs font-bold flex items-center gap-1.5 flex-shrink-0" style={{ backgroundColor: PEACH, color: NIGHT }} data-testid="quietroom-sar">
            <FileArrowDown size={12} weight="bold" /> SAR export
          </button>
        </div>
      </main>
    </div>
  );
}

// ---------- PRACTICE ----------
function PracticeTab() {
  return (
    <div>
      <div className="mb-8">
        <p className="text-xs font-mono uppercase tracking-widest mb-2" style={{ color: PEACH }}>April 2026</p>
        <h1 className="text-4xl md:text-5xl font-black tracking-tighter" style={{ color: PEARL }}>Practice at a glance.</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <PracticeStat label="Sessions delivered" value="38" sub="↑ 12% vs Mar" />
        <PracticeStat label="Package revenue" value="£3,420" sub="6 active packages" />
        <PracticeStat label="Late cancels charged" value="£300" sub="4 enforced · 1 waived" highlight />
        <PracticeStat label="SAR turn-around" value="2 days" sub="Last request: 8 Apr" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="p-5" style={{ backgroundColor: NIGHT_2 }}>
          <p className="text-[10px] font-mono uppercase tracking-widest font-bold mb-3" style={{ color: PEACH }}>Active packages — used vs total</p>
          <div className="grid gap-3">
            {[
              { n: "Sarah K.", u: 4, t: 6 },
              { n: "James M.", u: 1, t: 6 },
              { n: "Priya R.", u: 5, t: 6 },
              { n: "Tom B.", u: 3, t: 12 },
              { n: "Iris W.", u: 8, t: 8 },
            ].map((p) => (
              <div key={p.n} className="flex items-center gap-3">
                <span className="text-xs font-mono w-24 flex-shrink-0" style={{ color: TYPE_LIGHT }}>{p.n}</span>
                <div className="flex-1 h-2" style={{ backgroundColor: NIGHT }}>
                  <div className="h-full" style={{ width: `${(p.u / p.t) * 100}%`, backgroundColor: p.u === p.t ? PLUM : PEACH }} />
                </div>
                <span className="text-[10px] font-mono uppercase tracking-widest w-12 text-right" style={{ color: TYPE_MUTED }}>{p.u}/{p.t}</span>
              </div>
            ))}
          </div>
          <p className="text-xs mt-4" style={{ color: TYPE_MUTED }}><b style={{ color: PEACH }}>Iris W.</b> finished her package — auto-prompt sent to discuss renewal.</p>
        </div>

        <div className="p-5" style={{ backgroundColor: NIGHT_2 }}>
          <p className="text-[10px] font-mono uppercase tracking-widest font-bold mb-3" style={{ color: PEACH }}>Compliance status</p>
          <div className="grid gap-3">
            <ComplianceRow label="ICO registration" value="ZA888231" ok />
            <ComplianceRow label="PII data residency" value="UK only" ok />
            <ComplianceRow label="Encryption at rest" value="AES-256" ok />
            <ComplianceRow label="Audio retention" value="30-day expiry" ok />
            <ComplianceRow label="Last SAR drill" value="8 Apr · 2-day TAT" ok />
            <ComplianceRow label="Breach log" value="Empty · clean" ok />
          </div>
        </div>
      </div>

      <div className="p-5 border-2 border-dashed" style={{ borderColor: PEACH, backgroundColor: NIGHT_2 }}>
        <p className="text-[10px] font-mono uppercase tracking-widest font-bold mb-2" style={{ color: PEACH }}>What QuietRoom is NOT</p>
        <p className="text-base mb-2" style={{ color: PEARL }}>It is deliberately not a calendar.</p>
        <p className="text-sm" style={{ color: TYPE_LIGHT }}>Cliniko, Calendly, and Square Appointments do calendars perfectly. QuietRoom is the encrypted, regulator-aware bit they don't — your <b style={{ color: PEACH }}>notes, packages, audio, and SAR cover</b>. Pair them.</p>
      </div>

      {/* ReflectiveMind perk — bundled cross-product offering (styled to match ReflectiveMind's refreshed look) */}
      <div className="mt-6 border" style={{ borderColor: NIGHT_3 }} data-testid="quietroom-reflectivemind-perk">
        {/* Header strip — ReflectiveMind's own brand frame */}
        <div className="bg-white px-6 py-4 flex items-center justify-between border-b" style={{ borderColor: "#E5E7EB" }}>
          <div className="flex items-center gap-2.5">
            <div className="h-7 w-7 rounded-full border-2 flex items-center justify-center" style={{ borderColor: "#5EAEFF" }}>
              <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: "#5EAEFF" }} />
            </div>
            <div className="text-[15px] font-semibold tracking-tight" style={{ color: "#0A0A0A" }}>Reflective<span style={{ color: "#0570DE" }}>Mind</span></div>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#6B7280" }}>Bundled · included in your subscription</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-0">
          {/* LEFT — editorial pitch */}
          <div className="md:col-span-6 p-7 md:p-8 bg-white" style={{ color: "#0A0A0A" }}>
            <p className="text-[10px] font-mono uppercase tracking-widest mb-4" style={{ color: "#0570DE" }}>AI-powered reflective journaling</p>
            <h3 className="text-3xl md:text-4xl font-black tracking-tighter leading-[0.98] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Your clients think clearly.<br/><span style={{ color: "#0570DE" }}>Grow on purpose.</span>
            </h3>
            <p className="text-[15px] leading-relaxed mb-5" style={{ color: "#3F3F46" }}>
              Five quiet minutes a day, transformed by AI into the patterns that shape the next session. Voice journaling, sentiment curves, weekly insights — your clients get <b style={{ color: "#0A0A0A" }}>ReflectiveMind premium for free</b>, bundled with your QuietRoom subscription.
            </p>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border" style={{ borderColor: "#E5E7EB", backgroundColor: "#FAFAFA" }}>
              <div className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: "#22C55E" }} />
              <span className="text-xs font-mono" style={{ color: "#6B7280" }}>reflective-mind.org</span>
            </div>
          </div>

          {/* RIGHT — 3-step perk */}
          <div className="md:col-span-6 p-7 md:p-8 grid gap-4" style={{ backgroundColor: "#FAFAFA" }}>
            <p className="text-[10px] font-mono uppercase tracking-widest font-semibold" style={{ color: "#0A0A0A" }}>How the cross-product perk works</p>
            <RMPerkRow num="01" title="One-tap invite" body="From any client file, send a magic-link to the client's ReflectiveMind premium tier — voice journaling, AI insights, quarterly reviews, the lot." />
            <RMPerkRow num="02" title="Mood &amp; sleep flow into QuietRoom" body="Their reflections power the between-session check-in panel above — you walk into the next session already up to speed." />
            <RMPerkRow num="03" title="They keep using it after therapy ends" body="When their package finishes, ReflectiveMind stays free on the basic tier, premium discounted. Genuine duty of care, no hard cut-off." />
            <p className="text-[10px] font-mono uppercase tracking-widest mt-1" style={{ color: "#6B7280" }}>Coming Q3 · ReflectiveMind moves to freemium · QuietRoom subscribers carry the premium perk through to every active client.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function RMPerkRow({ num, title, body }) {
  return (
    <div className="flex gap-4 items-start pb-4 border-b last:border-b-0 last:pb-0" style={{ borderColor: "#E5E7EB" }}>
      <div className="h-10 w-10 flex-shrink-0 rounded-lg flex items-center justify-center border" style={{ backgroundColor: "#EFF6FF", borderColor: "#DBEAFE" }}>
        <span className="text-sm font-bold" style={{ color: "#0570DE" }}>{num}</span>
      </div>
      <div>
        <p className="text-[15px] font-semibold" style={{ color: "#0A0A0A" }}>{title}</p>
        <p className="text-[13px] mt-1 leading-relaxed" style={{ color: "#3F3F46" }} dangerouslySetInnerHTML={{ __html: body }} />
      </div>
    </div>
  );
}

function PracticeStat({ label, value, sub, highlight }) {
  return (
    <div className="p-4" style={{ backgroundColor: highlight ? PLUM : NIGHT_2, color: highlight ? PEACH : PEARL }}>
      <p className="text-[10px] font-mono uppercase tracking-widest font-bold" style={{ color: highlight ? PEACH : PEACH, opacity: highlight ? 1 : 0.85 }}>{label}</p>
      <p className="text-3xl font-black mt-1">{value}</p>
      <p className="text-[10px] font-mono uppercase tracking-widest mt-1" style={{ color: highlight ? "#FFD7C0" : TYPE_MUTED }}>{sub}</p>
    </div>
  );
}

function ComplianceRow({ label, value, ok }) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <CheckCircle size={14} weight="fill" style={{ color: ok ? PEACH : PLUM }} className="flex-shrink-0" />
      <span className="flex-1" style={{ color: TYPE_LIGHT }}>{label}</span>
      <span className="font-mono text-xs" style={{ color: PEARL }}>{value}</span>
    </div>
  );
}
